import { ShieldCheck } from "lucide-react";
import { Badge, Card, CardBody, EmptyState, PageHeader } from "@fellowflats/ui";
import { OkSchema } from "@fellowflats/contracts/account-writes";
import { ReportsSchema, type AdminReport } from "@fellowflats/contracts/admin";
import { api } from "../lib/api";
import { config } from "../lib/config";
import { useApi } from "../lib/use-api";
import { ActionButton, Loaded, day } from "../components/bits";

const reasonTone = { SCAM: "danger", FAKE: "danger", INAPPROPRIATE: "warning", SPAM: "warning", OTHER: "neutral" } as const;

export function Reports() {
  const state = useApi(() => api("GET", "/admin/reports", ReportsSchema));
  const act = (p: Promise<unknown>) => p.then(state.reload);
  return (
    <div className="space-y-8">
      <PageHeader title="Reports" description="Listings, profiles and reviews people flagged. Open first, newest first." />
      <Loaded state={state}>
        {(reports) => {
          const open = reports.filter((r) => r.status === "OPEN");
          const closed = reports.filter((r) => r.status !== "OPEN");
          return (
            <>
              {open.length === 0 ? (
                <EmptyState icon={ShieldCheck} title="Nothing to review" body="Reported listings and profiles appear here." />
              ) : (
                open.map((r) => <OpenReport key={r.id} r={r} act={act} />)
              )}
              {closed.length > 0 && (
                <section className="space-y-2">
                  <h2 className="font-semibold">Closed</h2>
                  {closed.map((r) => (
                    <p key={r.id} className="rounded-md border border-border px-3 py-2 text-sm">
                      <Badge tone={r.status === "RESOLVED" ? "success" : "neutral"}>{r.status.toLowerCase()}</Badge> {r.target?.label ?? "(deleted)"}{" "}
                      <span className="text-muted-foreground">
                        · {r.reason.toLowerCase()} · closed {day(r.resolvedAt)} by {r.resolvedByEmail ?? "-"}
                      </span>
                    </p>
                  ))}
                </section>
              )}
            </>
          );
        }}
      </Loaded>
    </div>
  );
}

function OpenReport({ r, act }: { r: AdminReport; act: (p: Promise<unknown>) => Promise<unknown> }) {
  const t = r.target;
  return (
    <Card>
      <CardBody className="space-y-3 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={reasonTone[r.reason as keyof typeof reasonTone] ?? "neutral"}>{r.reason.toLowerCase()}</Badge>
          <Badge>{r.targetType.toLowerCase()}</Badge>
          <span className="text-sm text-muted-foreground">
            reported {day(r.createdAt)} by {r.reporterEmail ?? "-"}
          </span>
        </div>
        <p>
          {t?.href ? (
            <a href={`${config.siteUrl}${t.href}`} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline">
              {t.label}
            </a>
          ) : (
            <span className="font-medium">{t?.label ?? "(deleted)"}</span>
          )}
          {t?.ownerStatus === "SUSPENDED" && (
            <Badge tone="danger" className="ml-2">
              owner suspended
            </Badge>
          )}
        </p>
        {r.details && <p className="text-sm text-muted-foreground">"{r.details}"</p>}
        <div className="flex flex-wrap gap-2">
          {t?.propertyId && (
            <ActionButton
              variant="danger"
              confirm="Take this listing down (archive it)?"
              run={() => act(api("POST", `/admin/listings/${t.propertyId}/archive`, OkSchema))}
            >
              Take listing down
            </ActionButton>
          )}
          {t?.reviewId && (
            <ActionButton variant="danger" run={() => act(api("PUT", `/admin/reviews/${t.reviewId}/status`, OkSchema, { status: "REMOVED" }))}>
              Hide review
            </ActionButton>
          )}
          {t?.ownerId && t.ownerStatus === "ACTIVE" && (
            <ActionButton
              variant="danger"
              confirm="Suspend this account? They will be signed out of everything they can change."
              run={() => act(api("PUT", `/admin/users/${t.ownerId}/status`, OkSchema, { status: "SUSPENDED" }))}
            >
              Suspend owner
            </ActionButton>
          )}
          <ActionButton variant="primary" run={() => act(api("POST", `/admin/reports/${r.id}/resolve`, OkSchema, { status: "RESOLVED" }))}>
            Resolved
          </ActionButton>
          <ActionButton variant="ghost" run={() => act(api("POST", `/admin/reports/${r.id}/resolve`, OkSchema, { status: "DISMISSED" }))}>
            Dismiss
          </ActionButton>
        </div>
      </CardBody>
    </Card>
  );
}
